import { ChangeEvent, useState } from "react";
import { RowData, ToastInfo } from "../../../../../types";
import { hasValidationErrors } from "../../../helpers";
import { DEFAULT_TOAST, TOAST_MESSAGES } from "../../../constants";
import { ALLOWED_TYPES, MAX_FILE_SIZE, UploadState } from "./constants";
interface ParseFileResult {
  data: RowData[];
  fileParseError?: ToastInfo;
}
interface ValidateFileResult {
  response: RowData[];
  toastInfo: ToastInfo;
}

interface UseFileUploadProps {
  parseFile: (file: File) => Promise<ParseFileResult>;
  validateFile: (data: RowData[]) => Promise<ValidateFileResult>;
  customValidationToastMessage?: ToastInfo;
}

export const useFileUpload = ({
  parseFile,
  validateFile,
  customValidationToastMessage,
}: UseFileUploadProps) => {
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>(UploadState.IDLE);
  const [validatedData, setValidatedData] = useState<RowData[]>([]);
  const [toastMessage, setToastMessage] = useState<ToastInfo>(DEFAULT_TOAST);

  const resetUpload = () => {
    setAttachedFile(null);
    setValidatedData([]);
    setToastMessage(DEFAULT_TOAST);
    setUploadState(UploadState.IDLE);
  };

  const removeFile = () => {
    setAttachedFile(null);
    setToastMessage(DEFAULT_TOAST);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setToastMessage(TOAST_MESSAGES.INVALID_FILE_TYPE());

      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setToastMessage(TOAST_MESSAGES.FILE_TOO_LARGE());

      event.target.value = "";
      return;
    }

    setAttachedFile(file);
    setToastMessage(DEFAULT_TOAST);
    setUploadState(UploadState.IDLE);

    event.target.value = "";
  };

  const handleValidate = async () => {
    if (!attachedFile) {
      setToastMessage(TOAST_MESSAGES.NO_FILE_SELECTED());
      return;
    }
    setUploadState(UploadState.VALIDATING);
    try {
      const { data, fileParseError } = await parseFile(attachedFile);
      if (fileParseError) {
        setValidatedData([]);
        setToastMessage(fileParseError);
        setUploadState(UploadState.PARSING_ERROR);
        return;
      }
      const { response, toastInfo } = await validateFile(data);
      setValidatedData(response);
      const validationToast =
        toastInfo.variant === "positive" && customValidationToastMessage
          ? customValidationToastMessage
          : toastInfo;
      setToastMessage(validationToast);
      setUploadState(UploadState.VALIDATION_COMPLETE);
    } catch {
      setValidatedData([]);
      setToastMessage(TOAST_MESSAGES.FILE_PROCESSING_ERROR());
      setUploadState(UploadState.PARSING_ERROR);
    }
  };

  const hasErrors =
    hasValidationErrors(validatedData) || validatedData.length === 0;

  return {
    attachedFile,
    uploadState,
    validatedData,
    setUploadState,
    toastMessage,
    hasErrors,
    setToastMessage,
    resetUpload,
    removeFile,
    handleFileChange,
    handleValidate,
  };
};
